import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  IconButton,
  TextField,
  Typography,
  Avatar,
  Collapse,
  Badge,
  Fade,
  CircularProgress,
  Chip,
  Tooltip
} from '@mui/material';
import {
  Chat as ChatIcon,
  Close as CloseIcon,
  Send as SendIcon,
  ExpandMore as ExpandMoreIcon,
  SmartToy as BotIcon,
  Person as PersonIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import axios from 'axios';

const KamChatBox = ({ context = {}, onThreadUpdate, sessionId, userId, onOpenChange }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: 'Hi! I\'m Kam, your Studio Sivad design assistant. How can I help you today?',
      timestamp: new Date().toISOString()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [threadId, setThreadId] = useState(null);
  const [hasNewMessages, setHasNewMessages] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Inject slide-in animation styles once
  useEffect(() => {
    const styleId = 'kam-chat-slide-animation';
    if (!document.getElementById(styleId)) {
      const styleSheet = document.createElement('style');
      styleSheet.id = styleId;
      styleSheet.textContent = `
        @keyframes slideInFromRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `;
      document.head.appendChild(styleSheet);
    }
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isExpanded) {
      setHasNewMessages(false);
      inputRef.current?.focus();
    }
  }, [isExpanded]);

  const handleToggle = () => {
    const newExpandedState = !isExpanded;
    setIsExpanded(newExpandedState);
    onOpenChange?.(newExpandedState);
  };

  const handleNewConversation = () => {
    setThreadId(null);
    setMessages([
      {
        role: 'assistant',
        content: 'Starting a new conversation. How can I help you?',
        timestamp: new Date().toISOString()
      }
    ]);
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: inputValue.trim(),
      timestamp: new Date().toISOString()
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const response = await axios.post('/api/assistant/design', {
        messages: [{ role: 'user', content: userMessage.content }],
        context,
        threadId,
        sessionId,
        userId
      });

      const assistantMessage = {
        role: 'assistant',
        content: response.data.message,
        timestamp: new Date().toISOString()
      };

      setMessages((prev) => [...prev, assistantMessage]);

      // Update thread ID if returned
      if (response.data.threadId) {
        setThreadId(response.data.threadId);
        onThreadUpdate?.(response.data.threadId);
      }

      // Show notification if chat is collapsed
if (!isExpanded) {
  setHasNewMessages(true);
}
} catch (error) {
  console.error('Chat error:', error);
  const errorMessage = {
    role: 'assistant',
    content: error.response?.data?.message || 'Sorry, I encountered an error. Please try again.',
    isError: true,
    timestamp: new Date().toISOString()
  };
  setMessages((prev) => [...prev, errorMessage]);
} finally {
  setIsLoading(false);
}
};

const handleKeyPress = (e) => {
if (e.key === 'Enter' && !e.shiftKey) {
  e.preventDefault();
  handleSendMessage();
}
};

return (
<Box sx={{ position: 'relative' }}>
  {/* Kam Chat Sidebar - Slides in from right */}
  {isExpanded && (
    <Paper
      elevation={8}
      sx={{
        position: 'fixed',
        right: 0,
        top: 64, // Below header (AppBar height)
        bottom: 0,
        width: 380,
        zIndex: 1200, // Below AppBar (1300) but above content
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 0,
        overflow: 'hidden',
        backgroundColor: '#111827',
        boxShadow: '-4px 0 24px rgba(0,0,0,0.5)',
        animation: 'slideInFromRight 0.3s ease-out'
      }}
    >
      {/* Chat Header */}
      <Box
        sx={{
          p: 2,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar
            sx={{
              bgcolor: 'rgba(255,255,255,0.3)',
              width: 40,
              height: 40
            }}
          >
            <BotIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
              Kam
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              Design Assistant
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="New Conversation">
            <IconButton
              size="small"
              onClick={handleNewConversation}
              sx={{ color: 'white' }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <IconButton
            size="small"
            onClick={handleToggle}
            sx={{ color: 'white' }}
          >
            <ExpandMoreIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Context Chips */}
      {(context.lookName || context.cycleLabel) && (
        <Box
          sx={{
            p: 1,
            borderBottom: '1px solid rgba(255,255,255,0.06)', // UPDATED
            display: 'flex',
            gap: 1,
            flexWrap: 'wrap'
          }}
        >
          {context.lookName && (
            <Chip
              label={`Look: ${context.lookName}`}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
          {context.cycleLabel && (
            <Chip
              label={`Cycle: ${context.cycleLabel}`}
              size="small"
              color="secondary"
              variant="outlined"
            />
          )}
        </Box>
      )}

      {/* Messages Area */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: 2,
          backgroundColor: '#0d1117', // UPDATED
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }}
      >
        {messages.map((message, index) => (
          <Fade in key={index} timeout={300}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: message.role === 'user' ? 'row-reverse' : 'row',
                gap: 1,
                alignItems: 'flex-start'
              }}
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: '#1f2937'
                }}
              >
                {message.role === 'user' ? <PersonIcon fontSize="small" /> : <BotIcon fontSize="small" />}
              </Avatar>
              <Paper
                elevation={1}
                sx={{
                  p: 1.5,
                  maxWidth: '75%',
                  borderRadius: 2,
                  bgcolor: message.isError
                    ? '#2d3748'                // UPDATED
                    : message.role === 'user'
                    ? '#667eea'
                    : '#1e2536',              // UPDATED
                  color:
                    message.role === 'user'
                      ? 'white'
                      : 'rgba(255,255,255,0.92)', // UPDATED
                  wordBreak: 'break-word'
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    lineHeight: 1.5
                  }}
                >
                  {message.content}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    mt: 0.5,
                    color: 'rgba(255,255,255,0.55)', // UPDATED
                    fontSize: '0.7rem'
                  }}
                >
                  {new Date(message.timestamp).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </Typography>
              </Paper>
            </Box>
          </Fade>
        ))}

        {/* Typing Indicator */}
        {isLoading && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: '#764ba2' }}>
              <BotIcon fontSize="small" />
            </Avatar>

            <Paper
              elevation={1}
              sx={{
                p: 1.5,
                borderRadius: 2,
                display: 'flex',
                gap: 0.5,
                alignItems: 'center',
                bgcolor: '#1e2536' // UPDATED
              }}
            >
              <CircularProgress size={16} />
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)' }}>
                Kam is thinking...
              </Typography>
            </Paper>
          </Box>
        )}

        <div ref={messagesEndRef} />
      </Box>

      {/* Input Area */}
      <Box
        sx={{
          p: 2,
          borderTop: '1px solid rgba(255,255,255,0.08)', // UPDATED
          backgroundColor: '#0d1117' // UPDATED
        }}
      >
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            inputRef={inputRef}
            fullWidth
            multiline
            maxRows={3}
            size="small"
            placeholder="Ask Kam about your design..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isLoading}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                color: 'white', // UPDATED
                '& textarea::placeholder': {
                  color: 'rgba(255,255,255,0.6)' // UPDATED
                }
              }
            }}
          />
          <IconButton
            color="primary"
            onClick={handleSendMessage}
            disabled={!inputValue.trim() || isLoading}
            sx={{
              bgcolor: '#667eea',
              color: 'white',
              '&:hover': {
                bgcolor: '#764ba2'
              },
              '&:disabled': {
                bgcolor: '#e0e0e0'
              }
            }}
          >
            <SendIcon />
          </IconButton>
        </Box>
        {threadId && (
          <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: 'rgba(255,255,255,0.55)' }}>
            Thread: {threadId.substring(0, 20)}...
          </Typography>
        )}
      </Box>
    </Paper>
  )}

  {/* Chat Toggle Button for Header */}
  <Badge
    badgeContent={hasNewMessages ? '!' : 0}
    color="error"
    overlap="circular"
  >
    <Tooltip title={isExpanded ? 'Close Kam Chat' : 'Chat with Kam'}>
      <IconButton
        onClick={handleToggle}
        sx={{
          color: 'white',
          transition: 'all 0.2s ease',
          '&:hover': {
            bgcolor: 'rgba(255,255,255,0.1)'
          }
        }}
      >
        {isExpanded ? <CloseIcon /> : <ChatIcon />}
      </IconButton>
    </Tooltip>
  </Badge>
</Box>
);
};

export default KamChatBox;